"use client";
import {
  EditorCommand,
  EditorCommandEmpty,
  EditorCommandItem,
  EditorCommandList,
  EditorCommandGroup,
  EditorContent,
  type EditorInstance,
  EditorRoot,
  ImageResizer,
  type JSONContent,
  handleCommandNavigation,
  handleImageDrop,
  handleImagePaste,
} from "novel";
import { useRef, useState } from "react";
import { enhancedExtensions } from "./extensions";
import { FloatingStatusToolbar } from "./selectors/floating-status-toolbar";

import GenerativeMenuSwitch from "./generative/generative-menu-switch";
import { uploadFn } from "./image-upload";
import { TextButtons } from "./selectors/text-buttons";
import { slashCommand, suggestionOptions } from "./slash-command";
import { useDebounce } from "@/hooks";
import { Divider } from "antd";
import { QuoteButtons } from "./selectors/quote-buttons";
import { HeadingSelector } from "./selectors/heading-selector";
import { NodeListSelector } from "./selectors/node-list-selector";
import { createStyles } from "antd-style";
import { cn } from "@/utils";
import GenerateSelector from "./selectors/generate-selector";
import TiptapProvider, { useTiptapContext } from "./tiptap-provider";
import TaskFloatingMenu from "./ui/TaskFloatingMenu";
import { recoverAiTasksFromDoc } from "@/lib/applyMarkdownInEditor";

const extensions = [...enhancedExtensions, slashCommand];

const TaskList = () => {
  const { tasks } = useTiptapContext(state => state)
  return (
    <>
      {tasks.map((task) => (
        <TaskFloatingMenu key={task.id} task={task} />
      ))}
    </>
  )
}
interface TiptapAdvancedEditorProps {
  value?: JSONContent;
  readOnly?: boolean;
  className?: string;
  onJsonChange?: (value: JSONContent) => void;
  onHtmlChange?: (html: string) => void;
  onBlur?: () => void;
}

/** Provider 内侧：能读 tasks store，打开稿时恢复未应用的 AI 浮层 */
const AdvancedEditorInner = ({ value, className, onJsonChange, onHtmlChange, onBlur }: Omit<TiptapAdvancedEditorProps, "readOnly">) => {
  const { styles } = useStyles();
  const editorRef = useRef<EditorInstance>();
  const recoveredRef = useRef(new Set<string>());
  const [openAI, setOpenAI] = useState(false);
  const addAiTask = useTiptapContext((s) => s.addAiTask);
  const getAiTasks = useTiptapContext((s) => s.getAiTasks);

  const debouncedUpdates = useDebounce(async (editor: EditorInstance) => {
    onJsonChange?.(editor.getJSON())
    onHtmlChange?.(editor.getHTML())
  }, 500);

  const onCreate = ({ editor }: { editor: EditorInstance }) => {
    editorRef.current = editor;
    for (const t of getAiTasks()) recoveredRef.current.add(t.id);
    recoverAiTasksFromDoc(editor, addAiTask, recoveredRef.current);
  }

  return (
      <div className={cn(styles, "relative tt-editor", className)}>
        <EditorRoot>
          <EditorContent
            onCreate={onCreate}
            initialContent={value}
            extensions={extensions}
            className="tt-editor__manuscript relative"
            editorProps={{
              handleDOMEvents: {
                keydown: (_view, event) => handleCommandNavigation(event),
              },
              handlePaste: (view, event) => handleImagePaste(view, event, uploadFn),
              handleDrop: (view, event, _slice, moved) => handleImageDrop(view, event, moved, uploadFn),
              attributes: {
                class:
                  "prose prose-lg dark:prose-invert prose-headings:font-title font-default focus:outline-none max-w-full",
              },
            }}
            onUpdate={({ editor }) => {
              debouncedUpdates(editor);
            }}
            onBlur={onBlur}
            slotAfter={<ImageResizer />}
          >
            <EditorCommand className="z-50 h-auto max-h-[330px] overflow-y-auto rounded-md border bg-background px-1 py-2 shadow-md transition-all">
              <EditorCommandEmpty className="px-2 text-muted-foreground">无匹配</EditorCommandEmpty>
              <EditorCommandList>
                {
                  suggestionOptions.map((item) => (
                    <EditorCommandGroup
                      className="pb-1 border-b last:border-b-0"
                      key={item.title}
                      heading={
                        <div className="block text-sm text-secondary tracking-wide font-bold px-3 pt-2">
                          {item.title}
                        </div>
                      }
                    >
                      {item.children.map((child) => (
                        <EditorCommandItem
                          value={`${child.title}`}
                          keywords={[...(child.searchTerms || []), item.title]}
                          onCommand={(val) => child.command?.(val)}
                          className="flex w-full items-center space-x-2 rounded-md px-2 py-3 text-left text-sm hover:bg-primary/10 aria-selected:bg-primary/10 cursor-pointer"
                          key={child.title}
                        >
                          <div className="flex-shrink-0 flex size-12 items-center justify-center rounded-md border border-muted bg-background">
                            {child.icon}
                          </div>
                          <div>
                            <p className="font-medium">{child.title}</p>
                            <p className="mt-1 text-xs text-secondary">{child.description}</p>
                          </div>
                        </EditorCommandItem>
                      ))}
                    </EditorCommandGroup>
                  ))
                }
              </EditorCommandList>
            </EditorCommand>

            <GenerativeMenuSwitch open={openAI} onOpenChange={setOpenAI}>
              <div className="flex gap-0.5 flex-wrap">
                <FloatingStatusToolbar />
              </div>
              <div className="flex gap-0.5 flex-wrap divide-x">
                <div className="px-0.5 py-0.5 rounded-md flex items-center empty:hidden pointer-events-auto nc-editor-action-bubble border border-[var(--tt-line,rgba(60,50,40,0.12))] bg-[var(--tt-surface,#faf9f6)] text-[var(--tt-ink,#2c2a26)] shadow-sm">
                  <TextButtons />
                  <Divider type="vertical" className="h-full mx-0.5 top-0 bg-[var(--tt-line,rgba(60,50,40,0.18))]" />
                  <QuoteButtons />
                  <HeadingSelector />
                  <NodeListSelector />
                </div>
              </div>
              <GenerateSelector />
            </GenerativeMenuSwitch>
            <TaskList />
          </EditorContent>
        </EditorRoot>
      </div>
  );
};

const TiptapAdvancedEditor = ({ value, readOnly, className, onJsonChange, onHtmlChange, onBlur }: TiptapAdvancedEditorProps) => {
  void readOnly;
  return (
    <TiptapProvider tasks={[]}>
      <AdvancedEditorInner
        value={value}
        className={className}
        onJsonChange={onJsonChange}
        onHtmlChange={onHtmlChange}
        onBlur={onBlur}
      />
    </TiptapProvider>
  );
};

const useStyles = createStyles(({ css, token }) => (
  css`
    color: ${token.colorText};
    --nc-manuscript-text-indent: 2ch;
    --nc-manuscript-paragraph-spacing: 0.50em;
    --nc-manuscript-placeholder-color: ${token.colorTextQuaternary};
    --nc-manuscript-text-size: 1.20rem;
    --nc-manuscript-line-height: 1.70;
    .tt-nc-block-element{
      --nc-manuscript-text-indent: 0em;
      --nc-manuscript-paragraph-indentation: 0em;
      --nc-manuscript-paragraph-spacing: 0.375em;
      line-height: 1.5;
      font-size: 16px
    }
  .tt-editor__manuscript{
    font-size: var(--nc-manuscript-text-size);
    line-height: var(--nc-manuscript-line-height);
    /* 宋体正文；数字用齐线等宽，避免西文旧式衬线数字难看 */
    font-family: var(--tt-serif, "Songti SC", "STSong", "Noto Serif SC", ui-serif, Georgia, serif);
    font-variant-numeric: lining-nums tabular-nums;
    font-feature-settings: "lnum" 1, "tnum" 1;
    ul, ol {
      margin-top: 1em;
      margin-bottom: 1em;
      padding-left: calc(2em + 2ch);
      line-height: inherit;
    }
    li > p:first-of-type {
      text-indent: 0px;
    }
    li + li {
      margin-top: var(--nc-manuscript-paragraph-spacing);
    }
    p+p, ol+p, ul+p {
      text-indent: var(--nc-manuscript-text-indent);
      margin-top: var(--nc-manuscript-paragraph-spacing);
    }

    .is-empty::before {
        content: attr(data-placeholder);
        float: left;
        color: var(--nc-manuscript-placeholder-color);
        pointer-events: none;
        height: 0;
    }
  }

  .ai-generating { color: var(--tt-accent, #2f4a6a); }
  .ai-generated { color: var(--tt-ink, #2c2a26); }

  .node-aiGeneration.ProseMirror-selectednode{
    box-shadow: 0 0 0 1px color-mix(in srgb, var(--tt-accent, #2f4a6a) 55%, transparent),
      0 0 0 5px color-mix(in srgb, var(--tt-accent, #2f4a6a) 12%, transparent);
    border-radius: 8px;
  }
  `
));

export default TiptapAdvancedEditor;
